"use client";

/**
 * Блок «Истории» (как в инстаграм) — вместо логотипов брендов. Горизонтальная
 * карусель прямоугольников (превью = фон первого слайда + подпись). Тап →
 * полноэкранный просмотрщик: прогресс-бары по слайдам, таймер автоперехода,
 * тап слева/справа — назад/вперёд, зажатие — пауза, X/Esc — закрыть, CTA-кнопка.
 * Контент и стили задаются в админке (раздел «Сайт Автопилот → Истории»),
 * хранятся в rent_site_global.data.stories. Сайт рендерит RU (фолбэк на EN).
 */

import Img from '@/components/Img';
import { useCallback, useEffect, useRef, useState } from "react";
import CarouselArrow from "./CarouselArrow";
import type { StoriesSettings, Story, StorySlide } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

export default function Stories({ stories }: { stories?: StoriesSettings }) {
  const { t, locale } = useLocale();
  // Текст по локали: en → *_en (фолбэк на *_ru), иначе *_ru (фолбэк на *_en).
  const ru = (a?: string, b?: string) =>
    locale === "en"
      ? (b && b.trim() ? b : a && a.trim() ? a : "")
      : (a && a.trim() ? a : b && b.trim() ? b : "");
  // Картинка слайда: en → EN → RU → legacy; ru → RU → EN → legacy.
  const img = (sl: StorySlide) =>
    (locale === "en"
      ? sl.image_en || sl.image_ru
      : sl.image_ru || sl.image_en) || sl.image || "";
  const hasContent = (sl: StorySlide) => !!(img(sl) || sl.title_ru || sl.title_en);
  const items = (stories?.items || []).filter((s) => s.enabled !== false && (s.slides || []).some(hasContent));
  const durationMs = Math.max(2, stories?.duration_sec || 5) * 1000;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [open, setOpen] = useState<number | null>(null); // индекс открытой истории
  const [slide, setSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    el?.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => { el?.removeEventListener("scroll", updateArrows); window.removeEventListener("resize", updateArrows); };
  }, [updateArrows, items.length]);

  const curStory: Story | null = open !== null ? items[open] || null : null;
  const slides: StorySlide[] = (curStory?.slides || []).filter(hasContent);
  const curSlide = slides[slide] || null;

  const openStory = (i: number) => { track("story_open"); setOpen(i); setSlide(0); setProgress(0); setPaused(false); };
  const close = useCallback(() => { setOpen(null); setSlide(0); setProgress(0); }, []);

  const next = useCallback(() => {
    setProgress(0);
    if (slide < slides.length - 1) { setSlide(slide + 1); return; }
    // последний слайд истории → следующая история
    if (open !== null && open < items.length - 1) { setOpen(open + 1); setSlide(0); return; }
    close();
  }, [slide, slides.length, open, items.length, close]);

  const prev = useCallback(() => {
    setProgress(0);
    if (slide > 0) { setSlide(slide - 1); return; }
    if (open !== null && open > 0) {
      const pi = open - 1;
      setOpen(pi);
      setSlide(Math.max(0, ((items[pi]?.slides || []).filter(hasContent).length) - 1));
    }
  }, [slide, open, items]);

  // Таймер автоперехода текущего слайда.
  useEffect(() => {
    if (open === null || paused || slides.length === 0) return;
    const tick = 50;
    const id = setInterval(() => {
      setProgress((p) => {
        const np = p + tick / durationMs;
        if (np >= 1) { next(); return 0; }
        return np;
      });
    }, tick);
    return () => clearInterval(id);
  }, [open, slide, paused, durationMs, next, slides.length]);

  // Esc — закрыть, стрелки — навигация.
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, close, next, prev]);

  if (!stories?.enabled || items.length === 0) return null;

  return (
    <div className="relative">
      {/* карусель превью-прямоугольников */}
      <div ref={trackRef} className="flex snap-x gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((s, i) => {
          const cover = (s.slides || []).map(img).find(Boolean) || "";
          const label = ru(s.label_ru, s.label_en);
          return (
            <button
              key={s.id || i}
              type="button"
              onClick={() => openStory(i)}
              title={label}
              className="group relative aspect-[9/16] h-[224px] w-auto shrink-0 snap-start cursor-pointer overflow-hidden rounded-2xl bg-surface-2 shadow-[0_4px_14px_rgba(20,29,69,0.12)] transition hover:shadow-[0_8px_22px_rgba(20,29,69,0.2)]"
            >
              {cover ? (
                <Img src={cover} alt={label} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.05]" />
              ) : null}
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/15" />
              {label ? (
                <span className="absolute inset-x-2.5 bottom-2.5 line-clamp-2 text-left text-[13px] font-semibold leading-tight text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">{label}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {canLeft && (
        <CarouselArrow direction="left" ariaLabel={t("a11y.prev")} onClick={() => trackRef.current?.scrollBy({ left: -280, behavior: "smooth" })}
          className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />
      )}
      {canRight && (
        <CarouselArrow direction="right" ariaLabel={t("a11y.next")} onClick={() => trackRef.current?.scrollBy({ left: 280, behavior: "smooth" })}
          className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />
      )}

      {/* полноэкранный просмотрщик */}
      {open !== null && curSlide && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90" onClick={close}>
          <div
            className="relative h-full w-full overflow-hidden bg-black sm:h-[90vh] sm:max-h-[90vh] sm:w-auto sm:aspect-[9/16] sm:rounded-2xl"
            style={{ containerType: "inline-size" }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={() => setPaused(true)}
            onPointerUp={() => setPaused(false)}
            onPointerLeave={() => setPaused(false)}
          >
            {/* фон слайда (картинка для текущего языка) */}
            {img(curSlide) ? (
              <Img src={img(curSlide)} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: curSlide.image_position || "center" }} />
            ) : (
              <div className="absolute inset-0" style={{ background: curSlide.bg_color || "#0039A6" }} />
            )}
            {/* настраиваемое затемнение для читаемости */}
            <div aria-hidden className="absolute inset-0" style={{ background: curSlide.bg_color || "#000", opacity: (curSlide.overlay ?? 30) / 100 }} />

            {/* прогресс-бары по слайдам */}
            <div className="absolute inset-x-3 top-3 z-20 flex gap-1">
              {slides.map((_, si) => (
                <span key={si} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/35">
                  <span className="block h-full rounded-full bg-white" style={{ width: si < slide ? "100%" : si === slide ? `${Math.min(100, progress * 100)}%` : "0%" }} />
                </span>
              ))}
            </div>

            {/* подпись истории + закрыть */}
            <div className="absolute inset-x-3 top-7 z-20 flex items-center justify-between">
              <span className="text-[14px] font-semibold text-white drop-shadow">{ru(curStory?.label_ru, curStory?.label_en)}</span>
              <button type="button" aria-label={t("common.close")} onClick={close} className="flex size-9 items-center justify-center rounded-full bg-black/30 text-white">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>

            {/* зоны тапа: левая треть — назад, правая — вперёд */}
            <button type="button" aria-label={t("a11y.prev")} onClick={prev} className="absolute inset-y-0 left-0 z-10 w-1/3 cursor-pointer" />
            <button type="button" aria-label={t("a11y.next")} onClick={next} className="absolute inset-y-0 right-0 z-10 w-2/3 cursor-pointer" />

            {/* контент слайда — свободно позиционируется (content_x/content_y, %) */}
            <div
              className="pointer-events-none absolute z-20 flex flex-col gap-[3cqw]"
              style={{
                left: `${curSlide.content_x ?? 50}%`,
                top: `${curSlide.content_y ?? 80}%`,
                transform: "translate(-50%, -50%)",
                width: `${curSlide.content_w ?? 84}%`,
                color: curSlide.text_color || "#fff",
                textAlign: curSlide.content_align || "left",
                alignItems: curSlide.content_align === "center" ? "center" : curSlide.content_align === "right" ? "flex-end" : "flex-start",
              }}
            >
              {ru(curSlide.title_ru, curSlide.title_en) ? (
                <h3 className="text-[7cqw] font-bold leading-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">{ru(curSlide.title_ru, curSlide.title_en)}</h3>
              ) : null}
              {ru(curSlide.subtitle_ru, curSlide.subtitle_en) ? (
                <p className="text-[4.2cqw] leading-snug opacity-95 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">{ru(curSlide.subtitle_ru, curSlide.subtitle_en)}</p>
              ) : null}
              {ru(curSlide.button_text_ru, curSlide.button_text_en) && curSlide.button_link ? (
                <a
                  href={curSlide.button_link}
                  onClick={() => track("story_cta_click")}
                  className="pointer-events-auto mt-[1cqw] inline-flex items-center justify-center rounded-[3cqw] px-[6cqw] py-[3.2cqw] text-[4.2cqw] font-semibold text-white transition hover:opacity-90"
                  style={{ background: curSlide.button_color || "#0039A6" }}
                >
                  {ru(curSlide.button_text_ru, curSlide.button_text_en)}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
